import React, { useState } from "react";
import useFetch from "../../../hooks/useFetch";
import PaginationControl from "../../../components/Table/PaginationControl";
import useDebouncedSearchInput from "../../../hooks/useDebouncedSearch";
import TableHeader from "../../../components/Table/TableHeader";
import TableRow from "../../../components/Table/TableRow";
import SendPushNotificationModal from "./components/SendPushNotificationModal";
import NotificationView from "./components/NotificationView";
import LabeledSelect from "../../../components/Select/LabeledSelect";
import { useAuthHeader } from "react-auth-kit";
import { toast } from "react-toastify";
import { FaCopy, FaEdit, FaTrashAlt } from "react-icons/fa";
import { deleteNotification } from "./api/notificationsApi";
import EditPushNotificationButton from "./components/EditPushNotification";
import { BsFillSendFill } from "react-icons/bs";

function PushNotifications() {
  const [searchSetting, setSearchSetting] = useState({
    page: 0,
    size: 10,
    query: "",
    sortBy: "ID",
    orderBy: "DESC",
  });
  const query = `/v1/admin/notifications/push-notifications?query=${searchSetting.query}&page=${searchSetting.page}&pageSize=${searchSetting.size}&sortBy=${searchSetting.sortBy}&orderBy=${searchSetting.orderBy}`;

  const { data, loading, refresh } = useFetch(query);

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <NotificationHeader refresh={refresh} />
      </div>
      <div className="bg-white p-6 border border-gray-200">
        <NotificationSearchActions
          setSearchSetting={setSearchSetting}
          searchSetting={searchSetting}
        />
        {loading ? (
          <span>Loading...</span>
        ) : (
          <div>
            <NotificationsTable data={data} refresh={refresh} />
            <PaginationControl
              searchSetting={searchSetting}
              setSearchSetting={setSearchSetting}
              paginationSettings={data.pageMetadata}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const NotificationHeader = ({ refresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex w-full justify-between mb-2 items-center">
      <h1 className="text-xl">PUSH NOTIFICATIONS</h1>
      <button onClick={() => setIsOpen(true)} className="submit-btn uppercase">
        <BsFillSendFill />
        Send
      </button>
      <SendPushNotificationModal
        onSuccess={refresh}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );
};

const NotificationsTable = ({ data, refresh }) => {
  const [notificationView, setNotificationView] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const openView = (notification) => {
    setNotificationView(notification);
    setIsViewOpen(true);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse">
        <thead className="text-gray-800 text-sm bg-gray-100 h-10">
          <tr>
            <TableHeader name="ID" className="w-[5%]" />
            <TableHeader name="Title" className="w-[25%]" />
            <TableHeader name="Type" className="w-[15%]" />
            <TableHeader name="Country" className="w-[10%]" />
            <TableHeader name="Entity" className="w-[10%]" />
            <TableHeader name="Link" className="w-[10%]" />
            <TableHeader name="Expires At" className="w-[15%]" />
            <TableHeader name="Actions" className="w-[10%]" />
          </tr>
        </thead>
        <tbody>
          {data.data.map((notification, i) => (
            <tr
              key={i}
              onClick={() => openView(notification)}
              className="border-b text-xs hover:bg-gray-50 duration-300"
            >
              <TableRow value={notification.id} />
              <TableRow value={notification.title} />
              <TableRow
                value={
                  <span className="px-2 py-0.5 bg-sky-100 text-xs">
                    {notification.entityType}
                  </span>
                }
              />
              <TableRow
                value={
                  notification.countryCode ? (
                    <span className="px-2 py-0.5 bg-lime-100 text-xs">
                      {notification.countryCode}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-orange-100 text-xs">
                      Not set
                    </span>
                  )
                }
              />
              <TableRow
                value={
                  notification.entityId && notification.entityId > 0 ? (
                    notification.entityId
                  ) : (
                    <span className="px-2 py-0.5 bg-orange-100 text-xs">
                      Not set
                    </span>
                  )
                }
              />
              <TableRow
                value={
                  notification.link ? (
                    <span className="px-2 py-0.5 bg-lime-100 text-xs">
                      Included
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-orange-100 text-xs">
                      Not set
                    </span>
                  )
                }
              />
              <TableRow
                value={new Date(notification.expiresAt).toLocaleDateString(
                  "uk-UA",
                )}
              />
              <TableRow
                className="w-[20%]"
                value={
                  <div onClick={(e) => e.stopPropagation()}>
                    <NotificationActions
                      notification={notification}
                      refresh={refresh}
                    />
                  </div>
                }
              />
            </tr>
          ))}
        </tbody>
      </table>
      <NotificationView
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        notification={notificationView}
      />
    </div>
  );
};

const NotificationSearchActions = ({ searchSetting, setSearchSetting }) => {
  const [searchInput, setSearchInput] = useState("");
  useDebouncedSearchInput(searchInput, setSearchSetting, "query");

  const handleSearchSettingsChange = (e) => {
    const { name, value } = e.target;
    setSearchSetting((prev) => ({
      ...prev,
      [name]: value,
      page: 0,
    }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4 mb-6 w-full items-end">
      <div className="w-full">
        <div className="relative">
          <label className="relative text-[10px] text-gray-700 block">
            Search
            <input
              type="search"
              placeholder="Search stores..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="block w-full p-2 pl-2 text-xs text-black border border-gray-300 focus:outline-none "
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <LabeledSelect
          label="Sort by"
          name="sortBy"
          value={searchSetting.sortBy}
          onChange={handleSearchSettingsChange}
          options={[
            { label: "Identifier", value: "ID" },
            { label: "Title", value: "TITLE" },
            { label: "Created At", value: "CREATED_AT" },
            { label: "Expires At", value: "EXPIRES_AT" },
          ]}
        />

        <LabeledSelect
          label="Order by"
          name="orderBy"
          value={searchSetting.orderBy}
          onChange={handleSearchSettingsChange}
          options={[
            { label: "Descending", value: "DESC" },
            { label: "Ascending", value: "ASC" },
          ]}
        />
      </div>
    </div>
  );
};

const NotificationActions = ({ notification, refresh }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isResendOpen, setIsResendOpen] = useState(false);

  const authHeader = useAuthHeader();

  const handleDelete = async () => {
    if (
      !window.confirm(`Are you sure you want to delete "${notification.id}"?`)
    )
      return;
    try {
      await deleteNotification(notification.id, authHeader());
      toast.success("Notification has been deleted");
      refresh();
    } catch (err) {
      toast.error("Failed to delete notification");
    }
  };

  return (
    <>
      <div className="flex gap-4 justify-left items-center text-xl">
        <button
          onClick={() => setIsEditOpen(true)}
          title="Edit"
          className="text-sky-500 hover:text-sky-700"
        >
          <FaEdit />
        </button>

        <button
          onClick={() => setIsResendOpen(true)}
          title="Send copy"
          className="text-lime-500 hover:text-lime-600"
        >
          <FaCopy />
        </button>

        <button
          onClick={handleDelete}
          title="Delete"
          className="text-red-600 hover:text-red-600"
        >
          <FaTrashAlt />
        </button>
      </div>

      {isResendOpen && (
        <SendPushNotificationModal
          onSuccess={refresh}
          isOpen={isResendOpen}
          onClose={() => setIsResendOpen(false)}
          notification={notification}
        />
      )}
      {isEditOpen && (
        <EditPushNotificationButton
          notification={notification}
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            refresh();
          }}
        />
      )}
    </>
  );
};

export default PushNotifications;
